import { useState, useEffect } from 'react';
import { Eye, EyeOff, Key, CheckCircle, XCircle, AlertTriangle, Video } from 'lucide-react';
import { apiKeyService } from '../../services/apiKeyService';

export const ApiKeyManager = () => {
    const [apiKey, setApiKey] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [status, setStatus] = useState({ configured: false, loading: true });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchStatus();

        // FIX (FRONTEND-BUG-002): Listen for API key changes from other clients
        const handleKeyChange = () => {
            console.log('🔑 API key change event received, refreshing status');
            fetchStatus();
        };

        window.addEventListener('api-key:changed', handleKeyChange);
        return () => window.removeEventListener('api-key:changed', handleKeyChange);
    }, []);

    const fetchStatus = async () => {
        try {
            const data = await apiKeyService.getAPIKeyStatus();
            setStatus({ configured: data.configured, loading: false });
            if (data.configured) {
                setApiKey('••••••••••••••••••••••••••••••');
            } else {
                setApiKey('');
            }
        } catch (error) {
            setStatus({ configured: false, loading: false });
        }
    };

    const handleSave = async () => {
        if (!apiKey || apiKey.includes('•')) return;

        // FIX (FRONTEND-BUG-005): Client-side API key validation
        // Gemini API keys typically start with "AIza" and are 39 characters
        const apiKeyPattern = /^AIza[A-Za-z0-9_-]{35}$/;

        if (!apiKeyPattern.test(apiKey)) {
            setMessage({
                type: 'error',
                text: 'Invalid API key format. Gemini API keys should start with "AIza" and be 39 characters long.',
            });
            return;
        }

        try {
            setSaving(true);
            setMessage(null);
            await apiKeyService.setAPIKey(apiKey);
            setMessage({ type: 'success', text: '✓ API key validated and saved successfully!' });
            await fetchStatus();
            setApiKey('••••••••••••••••••••••••••••••');
            setShowKey(false);

            // Notify other clients (via sync service polling)
            window.dispatchEvent(new CustomEvent('api-key:changed'));
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to save API key',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!confirm('Remove your saved API key? You can add it again later.')) return;

        try {
            setSaving(true);
            await apiKeyService.removeAPIKey();
            setMessage({ type: 'success', text: 'API key removed successfully' });
            setApiKey('');
            setStatus({ configured: false, loading: false });

            // Notify other clients
            window.dispatchEvent(new CustomEvent('api-key:changed'));
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to remove API key' });
        } finally {
            setSaving(false);
        }
    };

    const handleFocus = () => {
        // FIX (FRONTEND-BUG-011): Better UX for masked input
        if (apiKey.includes('•')) {
            setApiKey('');
            setShowKey(true);
        }
    };

    return (
        <div className="card space-y-4">
            {/* Tutorial Prompt Banner */}
            <div className="p-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800">
                <div className="flex items-center gap-2 text-cyan-800 dark:text-cyan-200">
                    <Video className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">
                        <strong>Need help?</strong> Check out the step-by-step video tutorial in the <strong>Tutorials tab</strong> above to learn how to get your API key.
                    </span>
                </div>
            </div>

            <div>
                <h3 className="text-xl font-semibold flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary" />
                    Gemini API Configuration
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Snap Solver AI uses your own Gemini API key for privacy and unlimited usage.
                </p>
            </div>

            {status.loading ? (
                <div className="py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
            ) : (
                <>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-sm font-medium">API Key Status</span>
                            <div className="flex items-center gap-2">
                                {status.configured ? (
                                    <>
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <span className="text-sm font-medium text-green-600">Configured</span>
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="w-5 h-5 text-red-600" />
                                        <span className="text-sm font-medium text-red-600">Not Configured</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex gap-2">
                                <div className="flex-1 relative">
                                    <input
                                        type={showKey ? 'text' : 'password'}
                                        value={apiKey}
                                        onChange={(e) => setApiKey(e.target.value)}
                                        onFocus={handleFocus}
                                        placeholder="AIzaSy... (paste your API key)"
                                        className="input pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowKey(!showKey)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <button onClick={handleSave} disabled={saving || !apiKey || apiKey.includes('•')} className="btn-primary px-6">
                                    {saving ? 'Validating...' : 'Save'}
                                </button>
                            </div>

                            {status.configured && (
                                <button onClick={handleRemove} disabled={saving} className="btn-secondary text-sm">
                                    Remove Key
                                </button>
                            )}
                        </div>
                    </div>

                    {message && (
                        <div
                            className={`p-3 rounded-lg flex items-center gap-2 ${message.type === 'success'
                                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                                }`}
                        >
                            {message.type === 'success' ? (
                                <CheckCircle className="w-5 h-5" />
                            ) : (
                                <AlertTriangle className="w-5 h-5" />
                            )}
                            <p className="text-sm">{message.text}</p>
                        </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Don't have a key?{' '}
                        <a
                            href="https://aistudio.google.com/app/apikey"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Get one for free here
                        </a>
                    </p>
                </>
            )}
        </div>
    );
};
